import React, { Fragment, useMemo, useCallback } from 'react';
import { Grid, MenuItem, Paper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Form } from 'react-final-form';
import { styled } from '@mui/material/styles';
import { Checkboxes, Select, TextField } from 'mui-rff';
import _ from 'lodash';
//
import ButtonSubmit from 'components/common/ButtonSubmit';
import {
  composeValidators,
  required,
  isFloat,
  isInteger,
  maxValue,
} from 'utils/validate';
import {
  alertTypes,
  MANUAL_INTEGRATION,
  PAYMENT_ALERT,
  UBIDEX_GATEWAY,
} from '../../../../constants';
import EnhancedAutocomplete from 'components/common/EnhancedAutocomplete';
import {
  WL_FUNCTIONALITY_OPTIONS,
  WL_SUBSCRIPTION_TYPE_LABELS,
  CREATIVE_FILE_UPDATE_OPTIONS,
} from '../../../../constants/wl';
import NotificationConfigDetails from 'components/form/whitelabel/components/NotificationConfigDetails';
import { handleApiError } from 'utils/common';
import {
  useGetWLIDByIDQuery,
  useUpdateWLIDGeneralByIDMutation,
} from '../../../../store/api/wl.api';
import { useGetEndpointsDropdownQuery } from '../../../../store/api/endpoint.api';
import { PAYMENT_ALERT_DEFAULT_MESSAGE } from '../../../../constants/messages';

const FlexGridEndItem = styled(Grid)(({}) => ({
  display: 'flex',
  alignItems: 'flex-end',
}));
const FlexGridStartItem = styled(Grid)(({}) => ({
  display: 'flex',
  alignItems: 'flex-start',
}));

const initial = {
  paymentAlert: PAYMENT_ALERT,
  endpoints: [],
};

const integrations = [
  { label: 'Ubidex Gateway', value: UBIDEX_GATEWAY },
  { label: 'Manual Integration', value: MANUAL_INTEGRATION },
];

const maxRateLimitValue = maxValue(1000);

export default function Subscription({ id, onFormStateChanged, editable }) {
  const isUpdate = useMemo(() => !!id, [id]);
  const { enqueueSnackbar } = useSnackbar();
  const { data: generalInfo = initial, isSuccess: detailsFetched } =
    useGetWLIDByIDQuery(id, {
      skip: !id,
    });
  const { data: endpoints = [], isSuccess: endpointsFetched } =
    useGetEndpointsDropdownQuery();
  const [mutate] = useUpdateWLIDGeneralByIDMutation();
  const decorators = useMemo(
    () => [
      ({ subscribe }) =>
        subscribe(({ dirty }) => onFormStateChanged(dirty), { dirty: true }),
    ],
    [onFormStateChanged],
  );

  const subscriptionInitialValues = useMemo(() => {
    const initial = _.cloneDeep(generalInfo);
    if (_.isNil(_.get(initial, `alerts.${alertTypes.payment}.message`))) {
      _.set(
        initial,
        `alerts.${alertTypes.payment}.message`,
        PAYMENT_ALERT_DEFAULT_MESSAGE,
      );
    }
    return initial;
  }, [generalInfo]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        await mutate({ id, ...values }).unwrap();

        enqueueSnackbar('Success!', { variant: 'success' });
      } catch (e) {
        handleApiError(e);
      }
    },
    [mutate, id, enqueueSnackbar],
  );

  const getEndpointOptionColor = useCallback((option) => {
    switch (option.status) {
      case 'ACTIVE':
        return 'success';
      case 'PAUSED':
        return 'warning';
      case 'REMOVED':
        return 'error';
      default:
        return 'primary';
    }
  }, []);

  if (!id || !endpointsFetched || !detailsFetched) {
    return <></>;
  }
  return (
    <Form
      onSubmit={onSubmit}
      initialValues={subscriptionInitialValues}
      decorators={decorators}
      render={({ handleSubmit, submitting, values }) => {
        const isLastOfIntegration = values.integrations
          ? values.integrations.length === 1
          : false;

        return (
          <form onSubmit={handleSubmit} noValidate>
            <Paper style={{ padding: 16 }}>
              <Grid container alignItems='flex-start' spacing={2}>
                <FlexGridEndItem size={{ xs: 6 }}>
                  <Select
                    name='subscription'
                    label='Subscription'
                    multiple={false}
                    disabled={!editable}
                  >
                    {Object.entries(WL_SUBSCRIPTION_TYPE_LABELS).map(
                      ([value, label]) => (
                        <MenuItem value={value} key={value}>
                          {label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FlexGridEndItem>
                <FlexGridEndItem size={{ xs: 6 }}>
                  <TextField
                    id='qpsPlan'
                    name='qpsPlan'
                    label='Qps'
                    fullWidth
                    disabled={!editable}
                  />
                </FlexGridEndItem>
                <FlexGridEndItem size={{ xs: 6 }}>
                  <TextField
                    id='pricePlan'
                    name='pricePlan'
                    label='Price'
                    fullWidth
                    disabled={!editable}
                  />
                </FlexGridEndItem>
                <FlexGridEndItem size={{ xs: 6 }}>
                  {isUpdate && (
                    <Grid container columnSpacing={2} sx={{ width: '100%' }}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          id='maxBidCPM'
                          name='maxBidCPM'
                          label='Max Bid CPM'
                          fieldProps={{ validate: isFloat }}
                          fullWidth
                          disabled={!editable}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          id='maxBidCPC'
                          name='maxBidCPC'
                          label='Max Bid CPC'
                          fieldProps={{ validate: isFloat }}
                          fullWidth
                          disabled={!editable}
                        />
                      </Grid>
                    </Grid>
                  )}
                </FlexGridEndItem>
                <FlexGridEndItem size={{ xs: 6 }}>
                  {isUpdate ? (
                    <TextField
                      id='maxBrandNumbers'
                      name='maxBrandNumbers'
                      label='Max Number of Brands'
                      fieldProps={{ validate: isInteger }}
                      fullWidth
                      disabled={!editable}
                    />
                  ) : null}
                </FlexGridEndItem>
                <FlexGridEndItem size={{ xs: 6 }}>
                  {isUpdate && (
                    <Grid container columnSpacing={2} sx={{ width: '100%' }}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          id='60min'
                          name='reportsApiRateLimits.60min'
                          label='Reports API Rate Limit per 1h'
                          fieldProps={{
                            validate: composeValidators(
                              required,
                              isInteger,
                              maxRateLimitValue,
                            ),
                          }}
                          fullWidth
                          disabled={!editable}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          id='5min'
                          name='reportsApiRateLimits.5min'
                          label='Reports API Rate Limit per 5min'
                          fieldProps={{
                            validate: composeValidators(
                              required,
                              isInteger,
                              maxRateLimitValue,
                            ),
                          }}
                          fullWidth
                          disabled={!editable}
                        />
                      </Grid>
                    </Grid>
                  )}
                </FlexGridEndItem>

                <FlexGridStartItem
                  size={{ xs: 12, md: 6 }}
                  container
                  spacing={2}
                >
                  <div>
                    <Typography color='#00000099'>
                      Subscription notifications
                    </Typography>
                    <NotificationConfigDetails
                      disabled={!editable}
                      flagValue={values.alerts?.[alertTypes.payment]?.enabled}
                      flagName={`alerts.${alertTypes.payment}.enabled`}
                      secondaryFlagName={`alerts.${alertTypes.payment}.email`}
                      secondaryFlagLabel='Email Notification'
                      flagLabel='Payment notification'
                      dateLabel='End Date'
                      dateName={`alerts.${alertTypes.payment}.endDate`}
                      textName={`alerts.${alertTypes.payment}.message`}
                      startDateName={`startDate`}
                      textRows={3}
                      startDateDisabledOverride={!editable}
                    />
                  </div>
                </FlexGridStartItem>

                <FlexGridStartItem
                  size={{ xs: 12, md: 6 }}
                  container
                  spacing={2}
                >
                  <Grid size={{ xs: 6 }}>
                    <Checkboxes
                      label='Allow creatives file update'
                      name='allowedCreativesFileUpdateSources'
                      disabled={!editable}
                      formControlProps={{ disabled: !editable }}
                      data={CREATIVE_FILE_UPDATE_OPTIONS}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Checkboxes
                      label='Integrations'
                      name='integrations'
                      data={integrations.map((i) => ({
                        ...i,
                        disabled:
                          !editable ||
                          (isLastOfIntegration &&
                            values.integrations.includes(i.value)),
                      }))}
                      formControlProps={{ disabled: !editable }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Checkboxes
                      label='Functionality'
                      name='functionality'
                      disabled={!editable}
                      formControlProps={{ disabled: !editable }}
                      data={WL_FUNCTIONALITY_OPTIONS}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Checkboxes
                      label={'\u00A0'}
                      name='isAutoStopOnSubExpiration'
                      disabled={!editable}
                      formControlProps={{ disabled: !editable }}
                      data={{
                        label: 'Stop bidding on expiration',
                        value: false,
                      }}
                    />
                  </Grid>
                </FlexGridStartItem>

                <FlexGridEndItem size={{ xs: 12 }} container spacing={2}>
                  <EnhancedAutocomplete
                    disabled={
                      !values['integrations'].includes(UBIDEX_GATEWAY) ||
                      !editable
                    }
                    label='Endpoints'
                    name='endpoints'
                    getOptionColor={getEndpointOptionColor}
                    options={endpoints}
                    getOptionDisabled={({ value, status }) =>
                      status === 'REMOVED' && !values.endpoints.includes(value)
                    }
                  />
                </FlexGridEndItem>
              </Grid>
              <Grid
                container
                alignItems='flex-start'
                spacing={2}
                style={{ marginTop: 32 }}
              >
                <ButtonSubmit
                  name='save'
                  onSubmit={handleSubmit}
                  isPending={submitting}
                  disabled={!editable}
                  type='submit'
                />
              </Grid>
            </Paper>
          </form>
        );
      }}
    />
  );
}
